import React, { useState } from 'react';
import { Phone, Mail, MapPin, CheckCircle, Instagram, Facebook, Linkedin, Youtube, Send } from 'lucide-react';
import './Contact.css';

const Contact = () => {
  
  // WhatsApp configuration - easily changeable
  const WHATSAPP_NUMBER = '+44-7879987708';
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    grade: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatWhatsAppMessage = (data) => {
    const message = `


Name: ${data.name}
Email: ${data.email}
Phone: ${data.phone || 'Not provided'}
Grade Level: ${data.grade}
Subject: ${data.subject}

Hi, I would like to schedule a *free consultation* with SkillBourne Academy.
`;
    
    return encodeURIComponent(message);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Format and send to WhatsApp
    const whatsappMessage = formatWhatsAppMessage(formData);
    // Ensure number is digits only for wa.me
    const whatsappURL = `https://wa.me/${WHATSAPP_NUMBER.replace(/\D/g, '')}?text=${whatsappMessage}`;
    
    // Open WhatsApp in new tab
    window.open(whatsappURL, '_blank');
    
    setIsSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        grade: '',
        message: ''
      });
    }, 3000);
  };

  const subjects = [
    'Mathematics',
    'English',
    'Science (Biology)',
    'Science (Chemistry)', 
    'Science (Physics)',
    'History',
    'Geography',
    'Languages',
    'Other'
  ];

  const grades = [
    'Primary (Key Stage 1-2)',
    'Secondary (Key Stage 3)',
    'GCSE (Key Stage 4)',
    'A-Level (Key Stage 5)',
    '11+ Exam',
    'SATs',
    'IB',
    'Other'
  ];

  return (
    <section className="contact section" id="contact">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">
            Get Started <span className="gradient-text">Today</span>
          </h2>
          <p className="section-description">
            Book your free consultation and take the first step towards academic excellence
          </p>
        </div>

        <div className="contact-container">
          <div className="contact-grid">
            <div className="contact-form-container">
              <div className="form-card premium-card">
                {!isSubmitted ? (
                  <>
                    <h3 className="form-title">Book Your Free Consultation</h3>
                    <form onSubmit={handleSubmit} className="contact-form">
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="name" className="form-label">Full Name *</label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className="form-input"
                            required
                          />
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor="email" className="form-label">Email Address *</label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="form-input"
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="phone" className="form-label">Phone Number</label>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="form-input"
                          />
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor="grade" className="form-label">Grade Level *</label>
                          <select
                            id="grade"
                            name="grade"
                            value={formData.grade}
                            onChange={handleInputChange}
                            className="form-select"
                            required
                          >
                            <option value="">Select Grade Level</option>
                            {grades.map((grade, index) => (
                              <option key={index} value={grade}>{grade}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="subject" className="form-label">Subject of Interest *</label>
                        <select
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          className="form-select"
                          required
                        >
                          <option value="">Select Subject</option>
                          {subjects.map((subject, index) => (
                            <option key={index} value={subject}>{subject}</option>
                          ))}
                        </select>
                      </div>
                      
                      
                      <button
                        type="submit"
                        className="btn-primary form-submit"
                      >
                        Book Free Consultation
                        <Send className="btn-icon" />
                      </button>
                    </form>
                  </>
                ) : (
                  <div className="success-message">
                    <div className="success-icon">
                      <CheckCircle className="check-icon" />
                    </div>
                    <h3 className="success-title">Thank You!</h3>
                    <p className="success-text">
                      Your consultation request has been submitted successfully. 
                      We'll contact you within 24 hours to schedule your free assessment and demo class.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="contact-info">
              <div className="info-card premium-card">
                <h3 className="info-title">Contact Information</h3>
                <p className="info-description">
                  Ready to unlock your potential? Get in touch with us today!
                </p>
                
                <div className="contact-details">
                  <div className="contact-item">
                    <div className="contact-icon">
                      <Mail className="icon" />
                    </div>
                    <div className="contact-text">
                      <div className="contact-label">Email</div>
                      <div className="contact-value">info@skillbourne.com</div>
                    </div>
                  </div>
                  
                  <div className="contact-item">
                    <div className="contact-icon">
                      <Phone className="icon" />
                    </div>
                    <div className="contact-text">
                      <div className="contact-label">Phone</div>
                      <div className="contact-value">+44-7879987708</div>
                    </div>
                  </div>
                  
                  <div className="contact-item">
                    <div className="contact-icon">
                      <MapPin className="icon" />
                    </div>
                    <div className="contact-text">
                      <div className="contact-label">Location</div>
                      <div className="contact-value">United Kingdom</div>
                    </div>
                  </div>
                </div>
                
              </div>
            </div>
          </div>
        </div>

        {/* Social Media Section */}
        <div className="social-section">
          <div className="container">
            <h3 className="social-section-title">Our <span className="gradient-text">Socials</span></h3>
            <div className="social-cards">
              <a 
                href="https://www.instagram.com/skillbourneacademy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-card instagram"
              >
                <div className="social-card-icon">
                  <Instagram className="social-card-icon-svg" />
                </div>
                <h4 className="social-card-title">Instagram</h4>
              </a>
              
              <a 
                href="https://www.facebook.com/profile.php?id=61579068097405" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-card facebook"
              >
                <div className="social-card-icon">
                  <Facebook className="social-card-icon-svg" />
                </div>
                <h4 className="social-card-title">Facebook</h4>
              </a>
              
              <a 
                href="https://www.linkedin.com/company/skillbourne/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-card linkedin"
              >
                <div className="social-card-icon">
                  <Linkedin className="social-card-icon-svg" />
                </div>
                <h4 className="social-card-title">LinkedIn</h4>
              </a>
              
              <a 
                href="https://www.youtube.com/@skillbourneacademy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-card youtube"
              >
                <div className="social-card-icon">
                  <Youtube className="social-card-icon-svg" />
                </div>
                <h4 className="social-card-title">YouTube</h4>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
